import { useStorage } from '@vueuse/core'
import { clearCaches } from 'browserslist'
interface User {
  id?: number
  email?: string
  name?: string
  api_token?: string
  tenant_identifier?: string
  api_tokenV2?: string
  tenant_identifierV2?: string
  role_id?: number
}
export const useUserStore = defineStore('userStore', () => {
  const toast = useToast()

  const { apiBaseUrl } = useAppConfig()
  const user = ref(useStorage('user', {} as User, localStorage))
  const calendarCategories = ref()
  const hasSupervisor = ref<boolean | null>(null)
  const userName = ref('')
  const isUserNameInitializing = ref(false)
  const contactUserRelation = ref<number | null>(null)

  const contact = ref(
    useStorage('contact', {} as Contact & { token?: string; tenantId?: string }, localStorage)
  )

  const isLoggedIn = computed(() => !!user.value.api_token)
  const isLoggedInAsContact = computed(() => !!contact.value.id)
  const lastStatusCheck = ref(0)
  const STATUS_CHECK_INTERVAL_MS = 5 * 60 * 1000
  const userEmail = computed(() => user.value.email ?? '-')
  const userProfilePicture = computed(() =>
    user?.value?.id
      ? `https:\\/\\/my.1tool.com\\/crm_files\\/users\\/${user.value.id}\\/logo.jpg`
      : 'https:\\/\\/my.1tool.com\\/images\\/standard_picture.jpg'
  )
  const currentId = computed(() => (isLoggedInAsContact.value ? contact.value.id : user.value.id))

  const runtimeConfig = useRuntimeConfig()
  const tenantId = runtimeConfig.public.PFLEGERING_TENANT_ID

  async function login(phone: string, password: string): Promise<boolean> {
    const response = (await fetch(`${apiBaseUrl}auth/user`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Tenant': tenantId
      },
      body: JSON.stringify({
        phone,
        password
      }),
      cache: 'no-cache',
      mode: 'cors'
    }).catch(() => {
      toast.add({
        severity: 'error',
        summary: 'stores.user.error.serviceNotReachable.title',
        detail: 'stores.user.error.serviceNotReachable.description'
      })
    })) as any

    if (!response.ok) {
      throw new Error(response.status)
    }

    if (response.status === 200) {
      const responseText = await response.text()
      if (responseText) {
        const {
          api_token: apiToken,
          tenant_identifier: tenantIdentifier,
          role_id: roleId,
          id
        } = JSON.parse(responseText)

        user.value = {
          id,
          role_id: roleId,
          api_token: apiToken,
          tenant_identifier: tenantIdentifier
        }

        return true
      } else {
        return false
      }
    } else {
      return false
    }
  }

  function logout() {
    const chatStore = useChatStore()
    const websocketStore = useWebsocketStore()
    if (window) {
      if (window?.Echo) {
        window.Echo.disconnect()
        delete window.Echo
      }
      window?.localStorage?.clear()
    }
    websocketStore.setSocketId('')
    chatStore.clearChats()
    calendarCategories.value = null
    user.value = {}
    userName.value = ''
    contact.value = {} as Contact & { token?: string; tenantId?: string }
    contactUserRelation.value = null
    lastStatusCheck.value = 0
    clearCaches()
    navigateTo('/login/employee')
  }

  async function fetchCalendarCategories() {
    if (!user.value.id) return
    try {
      const response = await useApi(`calendar-categories?filter[user_id]=${user.value.id}`, 'GET')
      calendarCategories.value = response?.data ?? []
    } catch {}
  }

  function setHasSupervisor(hasSupervisorParam: boolean | null) {
    hasSupervisor.value = hasSupervisorParam
  }

  async function checkStatus(): Promise<boolean> {
    const now = Date.now()
    if (now - lastStatusCheck.value < STATUS_CHECK_INTERVAL_MS) return true
    lastStatusCheck.value = now

    try {
      if (isLoggedIn.value) {
        const response = await useApi(`users/${user.value.id}`, 'GET')
        if (!response?.data?.id) return false
        return response.data.status !== 0
      }
      if (isLoggedInAsContact.value) {
        const response = await useApi('css/profile', 'GET')
        if (!response?.data?.id) return false
        if (response.data.view === 0) return false
        return true
      }
    } catch {}
    return true
  }

  async function initializeUserName() {
    if (userName.value.length || isUserNameInitializing.value) return
    isUserNameInitializing.value = true
    if (isLoggedInAsContact.value) {
      userName.value = `${contact.value.vorname} ${contact.value.name}`
    } else if (isLoggedIn.value) {
      const userResponse = (await useApi(`users/${user.value.id}`, 'GET')).data
      userName.value = userResponse?.name || ''
    }
    isUserNameInitializing.value = false
  }

  return {
    user,
    currentId,
    userEmail,
    userName,
    userProfilePicture,
    isLoggedIn,
    calendarCategories,
    hasSupervisor,
    contact,
    isLoggedInAsContact,
    fetchCalendarCategories,
    setHasSupervisor,
    initializeUserName,
    checkStatus,
    login,
    logout,
    contactUserRelation
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useUserStore, import.meta.hot))
}
